import { onUnmounted, ref, watch, type Ref } from 'vue';

const BONUS_EMOJIS = ['✨', '🌟', '💫', '🎯', '⚡', '💎', '🔥', '🍀', '💥', '🎁'];

export type FloatingBonusTarget = {
  id: number;
  emoji: string;
  x: number;
  y: number;
};

type UseFloatingBonusOptions = {
  active: Ref<boolean>;
  containerRef: Ref<HTMLElement | null>;
  onBonus: () => void | Promise<void>;
  minIntervalMs?: number;
  maxIntervalMs?: number;
  minVisibleMs?: number;
  maxVisibleMs?: number;
};

export function useFloatingBonus(options: UseFloatingBonusOptions) {
  const targets = ref<FloatingBonusTarget[]>([]);
  let nextId = 1;
  let spawnTimer: ReturnType<typeof setTimeout> | null = null;
  const removeTimers = new Map<number, ReturnType<typeof setTimeout>>();

  function clearSpawnTimer() {
    if (spawnTimer) {
      clearTimeout(spawnTimer);
      spawnTimer = null;
    }
  }

  function clearTarget(id: number) {
    const timer = removeTimers.get(id);
    if (timer) {
      clearTimeout(timer);
      removeTimers.delete(id);
    }
    targets.value = targets.value.filter((target) => target.id !== id);
  }

  function scheduleSpawn() {
    clearSpawnTimer();
    if (!options.active.value) return;

    const minInterval = options.minIntervalMs ?? 2500;
    const maxInterval = options.maxIntervalMs ?? 5500;
    const delay = minInterval + Math.random() * (maxInterval - minInterval);

    spawnTimer = setTimeout(() => {
      spawnTarget();
      scheduleSpawn();
    }, delay);
  }

  function spawnTarget() {
    const container = options.containerRef.value;
    if (!container || !options.active.value) return;

    const rect = container.getBoundingClientRect();
    if (rect.width < 40 || rect.height < 40) return;

    const padding = 36;
    const x = padding + Math.random() * Math.max(0, rect.width - padding * 2);
    const y = padding + Math.random() * Math.max(0, rect.height - padding * 2);
    const id = nextId++;
    const emoji = BONUS_EMOJIS[Math.floor(Math.random() * BONUS_EMOJIS.length)] ?? '✨';

    targets.value = [...targets.value, { id, emoji, x, y }];

    const minVisible = options.minVisibleMs ?? 1000;
    const maxVisible = options.maxVisibleMs ?? 2000;
    const visibleMs = minVisible + Math.random() * (maxVisible - minVisible);

    const removeTimer = setTimeout(() => clearTarget(id), visibleMs);
    removeTimers.set(id, removeTimer);
  }

  function handleTargetClick(id: number) {
    clearTarget(id);
    void Promise.resolve(options.onBonus());
  }

  function stop() {
    clearSpawnTimer();
    for (const id of [...removeTimers.keys()]) {
      clearTarget(id);
    }
  }

  watch(
    options.active,
    (active) => {
      if (active) {
        scheduleSpawn();
      } else {
        stop();
      }
    },
    { immediate: true },
  );

  onUnmounted(stop);

  return {
    targets,
    handleTargetClick,
    stop,
  };
}
